import React from 'react';
import { useTranslation } from '../../../../hooks';
import config from '../../../../config/environment';
import { IMAGES } from '../../../../assets';
import styles from './DownloadSection.module.css';

/**
 * DownloadSection Component (Landing Feature)
 * 
 * Download section component for the landing page featuring:
 * - App store badges (Google Play, App Store)
 * - Web app access link
 * - QR code for easy mobile access
 * - Platform availability information
 * - CSS Modules for scoped styling
 * - Responsive design
 * 
 * @component
 * @example
 * return (
 *   <DownloadSection />
 * )
 */
const DownloadSection = () => {
  const { t, language } = useTranslation();
  
  const handleWebAppClick = (e) => {
    e.preventDefault();
    window.open('https://app.qaqaaiyataljiser.com', '_blank', 'noopener,noreferrer');
  };

  const handleAppStoreClick = (e) => {
    // Ensure the link works by explicitly opening the App Store URL
    e.preventDefault();
    const appStoreUrl = 'https://apps.apple.com/lb/app/بوابة-الليطاني/id6753323742';
    console.log('Opening App Store URL:', appStoreUrl);
    window.open(appStoreUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className={styles.download} id="download">
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.header}>
          {/* Logo Display */}
          <div className={styles.logoContainer}>
            <img 
              src={IMAGES.branding.logoMain} 
              alt={language === 'ar' ? 'شعار بوابة الليطاني' : 'Litani Gateway Logo'}
              className={styles.logo}
            />
          </div>
          
          <h2 className={styles.title}>
            {t.download.title}
          </h2>
          
          <p className={styles.description}>
            {t.download.description}
          </p>
        </div>

        {/* Download Options */}
        <div className={styles.downloadOptions}>
          <h3 className={styles.platformTitle}>
            {t.download.choosePlatform}
          </h3>
          
          {/* App Store Badges */}
          <div className={styles.appStoreBadges}>
            {/* Google Play */}
            <a
              className={styles.appStoreBadge}
              href={config.social.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={language === 'ar' ? 'افتح تطبيق Google Play' : 'Open on Google Play'}
            >
              <img 
                src={language === 'ar' 
                  ? IMAGES.badges.googlePlay.ar
                  : IMAGES.badges.googlePlay.en
                } 
                alt={language === 'ar' ? 'احصل عليه من Google Play' : 'Get it on Google Play'} 
              />
            </a>

            {/* App Store */}
            <a
              className={styles.appStoreBadge}
              href="https://apps.apple.com/lb/app/بوابة-الليطاني/id6753323742"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAppStoreClick}
              aria-label={language === 'ar' ? 'افتح تطبيق App Store' : 'Open on App Store'}
            >
              <img 
                src={language === 'ar' ? IMAGES.badges.appStore.ar : IMAGES.badges.appStore.en} 
                alt={language === 'ar' ? 'نزله من App Store' : 'Download on the App Store'} 
              />
            </a>
          </div>

          {/* Web App Option */}
          <div className={styles.webAppSection}>
            <p className={styles.webAppText}>
              {t.download.webAppText}
            </p>
            <a 
              href="https://app.qaqaaiyataljiser.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.webAppLink}
              onClick={handleWebAppClick}
              aria-label={language === 'ar' ? 'افتح التطبيق الويب' : 'Open web application'}
            >
              {t.download.openWebApp}
            </a>
          </div>
        </div>

        {/* QR Code Section */}
        <div className={styles.qrSection}>
          <h4 className={styles.qrTitle}>
            {t.download.qrTitle}
          </h4>
          <img 
            src={IMAGES.ui.qrCodeWebsite} 
            alt={language === 'ar' ? 'رمز QR للتطبيق الويب' : 'QR Code for Web App'}
            className={styles.qrCodeImage}
          />
          <p className={styles.qrDescription}>
            {t.download.qrDescription}
          </p>
        </div>

        <p className={styles.availabilityText}>
          {t.download.availableOn}
        </p>
      </div>
    </section>
  );
};

export default DownloadSection;